import { useState, useMemo, useCallback } from "react";
import { FormattedMessage, useIntl, IntlShape } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useFormatPreferredDatetime } from "hooks/useFormatPreferredDatetime";
import errorToast from "helpers/errorToast";
import Badge from "components/v2/Badge/Badge";
import Card from "components/v2/Card/Card";
import EmptyState from "components/v2/EmptyState/EmptyState";
import { ReactComponent as ClipboardCheck } from "assets/images/iconsV2/clipboard-check-grey.svg";
import { ReactComponent as ShipmentIcon } from "assets/images/iconsV2/updates-shipment.svg";
import { ReactComponent as QuoteRequestIcon } from "assets/images/iconsV2/updates-quote.svg";
import { ReactComponent as ChevronDownDoubleIcon } from "assets/images/iconsV2/chevron-down-double.svg";
import { ReactComponent as BookmarkIcon } from "assets/images/icons/Bookmark.svg";
import useGetBookmarks from "api/queries/user/useGetBookmarks";
import IconCircle from "components/v2/IconCircle/IconCircle";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Tabs from "components/v2/Tabs/Tabs";
import Tab from "components/v2/Tabs/components/Tab";

import "./BookmarksWidget.scss";
import BookmarkWidgetStatusBadge from "./components/BookmarkWidgetStatusBadge";

interface Option {
  label: string;
  value: string;
}

const tabOptions = (intl: IntlShape): Option[] => [
  {
    label: intl.formatMessage({
      id: "general.all",
      defaultMessage: "All",
    }),
    value: "all",
  },
  {
    label: intl.formatMessage({
      id: "general.quoteRequests",
      defaultMessage: "Quote requests",
    }),
    value: "quote_request",
  },
  {
    label: intl.formatMessage({
      id: "general.shipments",
      defaultMessage: "Shipments",
    }),
    value: "shipment",
  },
];

const renderIcon = (type: string) => {
  switch (type) {
    case "quote_request":
      return QuoteRequestIcon;
    case "shipment":
      return ShipmentIcon;
    default:
      return QuoteRequestIcon;
  }
};

function BookmarksWidget() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { data: bookmarks, isFetching } = useGetBookmarks({
    onError: (err) =>
      errorToast(
        err,
        intl.formatMessage({
          id: "dashboard.bookmarksWidget.couldNotRetrieveBookmarks",
          defaultMessage: "Could not retrieve bookmarks. Please try again later.",
        }),
      ),
  });
  const { formatPreferredDatetime } = useFormatPreferredDatetime("D MMM, HH:mm");

  const [activeTab, setActiveTab] = useState("all");
  const [showScrollBanner, setShowScrollBanner] = useState(true);

  const showAll = activeTab === "all";
  const showQuoteRequests = activeTab === "quote_request";
  const showShipments = activeTab === "shipment";

  const bookmarkedQuoteRequests = bookmarks?.[0]?.data?.map((bookmark) => ({
    ...bookmark,
    type: "quote_request",
  }));
  const bookmarkedShipments = bookmarks?.[1]?.data?.map((bookmark) => ({
    ...bookmark,
    type: "shipment",
  }));
  const allBookmarks = [...(bookmarkedQuoteRequests ?? []), ...(bookmarkedShipments ?? [])];

  const filteredBookmarks = useMemo(() => {
    if (!bookmarks?.length) return [];
    if (showAll) return allBookmarks;
    if (showQuoteRequests) return bookmarkedQuoteRequests;
    if (showShipments) return bookmarkedShipments;
    return [];
  }, [activeTab, bookmarks]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setShowScrollBanner(e.currentTarget.scrollTop === 0);
  }, []);

  const handleNavigation = (bookmark: typeof allBookmarks[number]) => {
    if (bookmark.type === "quote_request") navigate(`/shipment/${bookmark.shipment_reference_number}`);
    else navigate(`/shipment/${bookmark.shipment_reference_number}/tracking`);
  };

  const emptyStateComponent = (
    <EmptyState
      noActions
      customIcon={<IconCircle icon={ClipboardCheck} color="tertiary" size="lg" />}
      title={intl.formatMessage({
        id: "dashboard.bookmarksWidget.emptyBookmarks.title",
        defaultMessage: "No bookmarked items yet",
      })}
      description={intl.formatMessage({
        id: "dashboard.bookmarksWidget.emptyBookmarks.description",
        defaultMessage: "Bookmarks important quotes or shipments for easy tracking and quick access.",
      })}
    />
  );

  return (
    <Card borderRadius="md" shadow="xs" style={{ height: "100%" }}>
      <div className="bookmarks-widget">
        <div className="bookmarks-widget__header radius-md">
          <div className="bookmarks-widget__icon">
            <BookmarkIcon />
          </div>
          <div className="w-100 d-flex align-items-center">
            <div className="font-medium text-md text-primary mr-2">
              <FormattedMessage id="general.bookmarks" defaultMessage="Bookmarks" />
            </div>
            <div>
              {!isFetching && allBookmarks?.length > 0 && (
                <Badge size="sm" color="gray" type="category-badge" label={allBookmarks?.length} />
              )}
            </div>
          </div>

          {/* {(showQuoteRequests || showShipments) && (
            <Button
              className="no-wrap"
              variant="link-color"
              label={intl.formatMessage({ id: "general.viewAll", defaultMessage: "View all" })}
              onClick={() =>
                navigate(
                  showQuoteRequests ? "/quote-requests?status=bookmarked&page=1" : "/shipments?status=bookmarked&page=1",
                )
              }
            />
          )} */}
        </div>

        <div className="">
          <Tabs activeKey={activeTab} style={{ padding: "0px 20px" }} type="underline" onSelect={(key) => setActiveTab(key)}>
            {tabOptions(intl).map((option) => (
              <Tab key={option.value} eventKey={option.value} title={option.label} />
            ))}
          </Tabs>
        </div>

        {isFetching && (
          <div className="">
            <LoadingSkeleton rows={2} />
          </div>
        )}

        {!isFetching && (
          <div className="bookmarks-widget__content" onScroll={handleScroll}>
            {filteredBookmarks?.length === 0 ? (
              <div className="d-flex justify-content-center align-items-center h-100">{emptyStateComponent}</div>
            ) : (
              filteredBookmarks?.map((bookmark, index) => (
                <div
                  key={index}
                  className="bookmarks-widget__item cursor-pointer bg-primary-hover d-flex align-items-center"
                  onClick={() => handleNavigation(bookmark)}
                >
                  <div className="mr-lg">
                    <IconCircle icon={renderIcon(bookmark.type)} color="primary" size="md" />
                  </div>
                  <div className="w-100">
                    <div className="d-flex justify-content-between w-100 gap-sm">
                      <div className="d-flex align-items-center">
                        <div className="text-sm font-medium text-primary wrap-anywhere">
                          {bookmark.shipment_reference_number}
                          <span className="text-sm font-regular">
                            {bookmark.shipment_title && ` | ${bookmark.shipment_title}`}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-tertiary flex-none">
                        {formatPreferredDatetime(bookmark.shipment_updated_at || bookmark.shipment_created_at)}
                      </div>
                    </div>
                    <div className="bookmarks-widget__description text-sm text-secondary">
                      <BookmarkWidgetStatusBadge
                        bookmarkType={bookmark.type}
                        quoteRequestStatuses={bookmark.quote_request_statuses}
                        shipmentStatus={bookmark.shipment_status}
                        isLost={bookmark.is_lost}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
            {filteredBookmarks && filteredBookmarks.length > 5 && (
              <div className={`recent-updates-widget__scroll-banner ${showScrollBanner ? "visible" : "hidden"}`}>
                <ChevronDownDoubleIcon />
                <div className="text-xs font-medium text-secondary-v2">Scroll to view more</div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export default BookmarksWidget;
